import React, { useEffect, useState, useCallback } from 'react';
import { Box, Flex } from '@chakra-ui/layout';
import { useHistory, useParams } from 'react-router-dom';
import { BookingCalendar } from '../../components/Object/BookingCalendar/BookingCalendar';
import { ObjectFeatures } from '../../components/Object/ObjectFeatures';
import {
  fetchObject,
  getDefaultObjectParams,
  getObjectParamsFromRouteParams,
  isQualifiedToFetchWithExtendedParams,
} from '../../services/Object/ObjectService';
import { ObjectMainInformation } from '../../components/Object/ObjectMainInformation';
import { ObjectLocation } from '../../components/Object/ObjectLocation';
import {
  loadStateFromLocalStorage,
  saveStateToLocalStorage,
} from '../../services/LocalStorage/serveLocalStorageService';
import { ExtendedDetailsRoute } from '../../types/RoutesTypes';
import { fetchObjectVacations } from '../../services/ObjectVacations/ObjectVacationsService';
import { SingleObject } from '../../types/ObjectTypes';
import { Accordion } from '../../components/common/Accordion/Accordion';
import { ObjectSeasosPriceTable } from '../../components/Object/ObjectSeasosPriceTable';
import { ObjectParams } from '../../types/ParamsTypes';
import {
  getBookingRoute,
  getInitialBookingRouteParams,
} from '../../services/Booking/BookingService';
import { useMediaQuery } from '@chakra-ui/react';
import { omit } from 'ramda';
import { ObjectTexts } from '../../components/Object/ObjectTexts';
import { DataOptions, getDataOptions } from '../../services/DataOption/DataOptionsService';

export interface ObjectsViewProps {
  handleLoadingSpinner: (value: boolean) => void;
  isLoading: boolean;
}

export const ObjectView = (props: ObjectsViewProps): JSX.Element => {
  const { handleLoadingSpinner, isLoading } = props;
  const history = useHistory();
  const [object, setObject] = useState<SingleObject>();
  const [priceErrorMessage, setPriceErrorMessage] = useState<string>();
  const routeParams: ExtendedDetailsRoute = useParams();
  const [bookingRouteParams, setBookingRouteParams] = useState<ObjectParams>(
    getInitialBookingRouteParams(routeParams),
  );
  const [isTabletOrMobile] = useMediaQuery('(max-width: 767px)');
  const [dataOptions] = useState<DataOptions>(getDataOptions());
  
  const handleDateRangeChange = useCallback(
    (fromDate, toDate) => {
      if (object && fromDate && toDate) {
        setBookingRouteParams({ ...bookingRouteParams, fromDate, toDate });
        (async function() {
          const objectParams = getDefaultObjectParams({
            Object: object.baseData.uniqueId,
            fromDate,
            toDate,
          });

          if (routeParams.numberOfAdults) {
            objectParams.numberOfAdults = Number.parseInt(routeParams.numberOfAdults);
          }

          if (routeParams.numberOfChildren) {
            objectParams.numberOfChildren = Number.parseInt(routeParams.numberOfChildren);
          }

          try {
            const objectDetailData = await fetchObject(objectParams);
            setObject({ ...object, ...objectDetailData.data.data.result.bookings });
            setPriceErrorMessage(undefined);
            setTimeout(() => {
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth',
              });
            });
          } catch (e) {
            setPriceErrorMessage(
              'Der Preis konnte für diesen Zeitraum nicht abgefragt werden. Bitten versuchen Sie einen anderen Zeitraum',
            );
          }
        })();
      } else {
        (async function() {
          const objectParams = getObjectParamsFromRouteParams(routeParams);
          const objectParamsWithoutFromAndTo = { ...objectParams };
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.fromDate;
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.toDate;
          setObject(omit(['priceData'], object) as SingleObject);

          const objectVacationsData = await fetchObjectVacations(objectParamsWithoutFromAndTo);

          setObject({
            ...omit(['priceData'], object),
            ...objectVacationsData.data.data.result,
          });
          setPriceErrorMessage(undefined);
        })();
      }
    },
    [object, routeParams],
  );


  const handleRedirectToBooking = useCallback(() => {
    const bookingRoute = getBookingRoute(bookingRouteParams);
    history.push(bookingRoute);
  }, [bookingRouteParams]);

  useEffect(() => {
    handleLoadingSpinner(true);
    setBookingRouteParams(getInitialBookingRouteParams(routeParams));
    (async function() {
      const objectParams = getObjectParamsFromRouteParams(routeParams);
      if (isQualifiedToFetchWithExtendedParams(routeParams)) {
        try {
          const objectDetailData = await fetchObject(objectParams);
          const objectParamsWithoutFromAndTo = { ...objectParams };
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.fromDate;
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.toDate;
          const objectVacationsData = await fetchObjectVacations(objectParamsWithoutFromAndTo);
          bookingRouteParams.Object = objectVacationsData.data.data.result.baseData.uniqueId;
          setBookingRouteParams(bookingRouteParams);
          setObject({
            ...object,
            ...objectDetailData.data.data.result.bookings,
            vacationsData: objectVacationsData.data.data.result.vacationsData,
            seasonsPriceData: objectVacationsData.data.data.result.seasonsPriceData,
            baseData: objectVacationsData.data.data.result.baseData,
          });
          setPriceErrorMessage(undefined);
          handleLoadingSpinner(false);
        } catch (e) {
          const objectParamsWithoutFromAndTo = { ...objectParams };
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.fromDate;
          // @ts-ignore
          delete objectParamsWithoutFromAndTo.toDate;
          const objectVacationsData = await fetchObjectVacations(objectParamsWithoutFromAndTo);
          bookingRouteParams.Object = objectVacationsData.data.data.result.baseData.uniqueId;
          setBookingRouteParams(bookingRouteParams);
          setObject(objectVacationsData.data.data.result);
          setPriceErrorMessage(
            'Dieser Zeitraum ist ausgebucht. Bitte versuchen Sie einen anderen Zeitraum',
          );
          handleLoadingSpinner(false);
          setTimeout(() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth',
            });
          });
        }
      } else {
        const objectParamsWithoutFromAndTo = { ...objectParams };
        // @ts-ignore
        delete objectParamsWithoutFromAndTo.fromDate;
        // @ts-ignore
        delete objectParamsWithoutFromAndTo.toDate;
        const objectDetailData = await fetchObjectVacations(objectParamsWithoutFromAndTo);
        bookingRouteParams.Object = objectDetailData.data.data.result.baseData.uniqueId;
        setBookingRouteParams(bookingRouteParams);

        handleLoadingSpinner(false);
        setObject({ ...object, ...objectDetailData.data.data.result });
      }
    })();
  }, [
    routeParams.id,
    routeParams.fromDate,
    routeParams.toDate,
    routeParams.numberOfAdults,
    routeParams.numberOfChildren,
  ]);

  const HOLIDAY_BOOKING_HOME_BOOKMARK = 'HOLIDAY_BOOKING_HOME_BOOKMARK';

  const bookmarkFromLocalStorage: ExtendedDetailsRoute[] = loadStateFromLocalStorage(
    HOLIDAY_BOOKING_HOME_BOOKMARK,
  );

  const defaultBookmarkState = bookmarkFromLocalStorage ? bookmarkFromLocalStorage : [];

  const [bookmarks, setBookmarks] = useState(defaultBookmarkState);

  const handleBookmarkChange = useCallback(
    (value: ExtendedDetailsRoute, action?: string) => {
      let newValues = [...bookmarks];

      if (action === 'ADD') {
        newValues.push(value);
      } else {
        newValues = newValues.filter(el => el.id !== value.id);
      }

      setBookmarks(newValues);
      saveStateToLocalStorage(newValues, HOLIDAY_BOOKING_HOME_BOOKMARK);
    },
    [bookmarks],
  );

  const [switchBookingCalendarModal, setSwitchBookingCalendarModal] = useState(false);
  const handleBookingCalendarModal = useCallback((value = false) => setSwitchBookingCalendarModal(value), []);

  return (
    <>
      {!isLoading && object && (
        <Flex minH='100vh' flexDirection={'column'} alignItems={'flex-start'} gridGap={6}>
          <ObjectMainInformation
            baseData={object.baseData}
            textsData={object.textsData}
            imagesData={object.imagesData}
            price={object.priceData}
            isLoading={isLoading}
            handleBookmarkChange={handleBookmarkChange}
            bookmarks={bookmarks}
            bookingRouteParams={bookingRouteParams}
            bookmarkParams={routeParams}
            handleBookingCalendarModal={handleBookingCalendarModal}
          />

          {isTabletOrMobile && (
            <Accordion textBtn={'BESCHREIBUNG'}>
              <ObjectTexts textsData={object.textsData} />
            </Accordion>
          )}
          {!isTabletOrMobile && <ObjectTexts textsData={object.textsData} />}

          {(isTabletOrMobile && !dataOptions.hideEquipment) && (
            <Accordion textBtn={'AUSSTATTUNG'}>
              <ObjectFeatures isLoading={isLoading} features={object.featuresData} />
            </Accordion>
          )}
          {(!isTabletOrMobile && !dataOptions.hideEquipment) && (
            <ObjectFeatures isLoading={isLoading} features={object.featuresData} />
          )}

          {isTabletOrMobile && object.baseData.showMap && (
            <Accordion textBtn={'LAGE'}>
              <ObjectLocation baseData={object.baseData} textsData={object.textsData} />
            </Accordion>
          )}
          {!isTabletOrMobile && object.baseData.showMap && (
            <ObjectLocation baseData={object.baseData} textsData={object.textsData} />
          )}

          {(isTabletOrMobile && object.seasonsPriceData && !dataOptions.hidePriceTable) && (
            <Accordion textBtn={'ÜBERNACHTUNGSPREISE'}>
              <ObjectSeasosPriceTable seasonsPrice={object.seasonsPriceData} />
            </Accordion>
          )}
          {(!isTabletOrMobile && object.seasonsPriceData && !dataOptions.hidePriceTable) && (
            <ObjectSeasosPriceTable seasonsPrice={object.seasonsPriceData} />
          )}

          <BookingCalendar
            object={object}
            baseData={object.baseData}
            routeParams={routeParams}
            handleRedirectToBooking={handleRedirectToBooking}
            error={priceErrorMessage}
            handleDateRangeChange={handleDateRangeChange}
            isTabletOrMobile={isTabletOrMobile}
            switchBookingCalendarModal={switchBookingCalendarModal}
            handleBookingCalendarModal={handleBookingCalendarModal}
          />
        </Flex>
      )}
    </>
  );
};
